import { FormEvent, useMemo, useState } from "react";
import {
  ArrowRight,
  CheckCircle2,
  CircleAlert,
  CircleX,
  Copy,
  Globe2,
  RotateCcw,
  Search,
  ShieldCheck,
} from "lucide-react";
import { Link } from "wouter";
import {
  emailHealthEnabled,
  scanPublicEmailDns,
  type EmailHealthResult,
} from "./firebase";
import {
  blocklistToolContent,
  recordToolContent,
  ToolEducation,
} from "./FreeToolContent";

export type RecordToolKind = "dmarc" | "dkim" | "spf";
export type BlocklistToolKind = "domain" | "ip";

const recordTools = {
  dmarc: {
    name: "DMARC Record Checker",
    heading: "Check your DMARC record",
    description: "Find the public DMARC policy for a domain and review its enforcement, coverage, alignment and reporting settings.",
    ids: ["dmarc", "dmarc_enforcement", "dmarc_coverage", "dmarc_reporting", "dmarc_alignment", "dmarc_subdomains"],
  },
  dkim: {
    name: "DKIM Record Checker",
    heading: "Check your DKIM record",
    description: "Verify that a DKIM selector publishes a usable public key for your sending domain.",
    ids: ["dkim"],
  },
  spf: {
    name: "SPF Record Checker",
    heading: "Check your SPF record",
    description: "Inspect your SPF policy, default enforcement, deprecated mechanisms and DNS lookup budget.",
    ids: ["spf", "spf_enforcement", "spf_lookup_budget", "spf_deprecated"],
  },
} as const;

const relatedTools = [
  ["DMARC Record Checker", "/tools/dmarc-checker"],
  ["DKIM Record Checker", "/tools/dkim-checker"],
  ["SPF Record Checker", "/tools/spf-checker"],
  ["Domain Blocklist Checker", "/tools/domain-blocklist-checker"],
  ["IP Blocklist Checker", "/tools/ip-blocklist-checker"],
] as const;

function ToolLinks({ current }: { current: string }) {
  return (
    <section className="free-tool-related">
      <div className="container">
        <p>More free tools</p>
        <div>
          {relatedTools.filter(([, href]) => href !== current).map(([label, href]) => (
            <Link href={href} key={href}>{label}<ArrowRight size={15} /></Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function ToolStatusIcon({ status }: { status: "passed" | "warning" | "critical" }) {
  if (status === "passed") return <CheckCircle2 aria-hidden="true" />;
  if (status === "critical") return <CircleX aria-hidden="true" />;
  return <CircleAlert aria-hidden="true" />;
}

export function RecordCheckerPage({ kind }: { kind: RecordToolKind }) {
  const tool = recordTools[kind];
  const content = recordToolContent[kind];
  const path = `/tools/${kind}-checker`;
  const [domain, setDomain] = useState("");
  const [selector, setSelector] = useState("");
  const [result, setResult] = useState<EmailHealthResult | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState("");
  const checks = useMemo(
    () => result?.checks.filter((check) => (tool.ids as readonly string[]).includes(check.id)) ?? [],
    [result, tool.ids],
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoading(true);
    setError("");
    setResult(null);
    try {
      const next = await scanPublicEmailDns({
        domain,
        dkimSelector: kind === "dkim" ? selector : undefined,
      });
      setDomain(next.domain);
      setResult(next);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "The DNS lookup could not be completed.");
    } finally {
      setLoading(false);
    }
  }

  async function copy(value: string) {
    await navigator.clipboard.writeText(value);
    setCopied(value);
    window.setTimeout(() => setCopied(""), 1600);
  }

  return (
    <main className="free-tool-page">
      <section className="free-tool-hero">
        <div className="container free-tool-hero__grid">
          <div className="free-tool-hero__copy">
            <ShieldCheck aria-hidden="true" />
            <h1>{tool.heading}</h1>
            <p>{tool.description}</p>
            <ul>
              <li><CheckCircle2 size={17} />No account required</li>
              <li><CheckCircle2 size={17} />Public DNS records only</li>
              <li><CheckCircle2 size={17} />Clear remediation guidance</li>
            </ul>
          </div>
          <form className="free-tool-form" onSubmit={submit}>
            <div>
              <label htmlFor={`${kind}-domain`}>Domain name</label>
              <div className="free-tool-input">
                <Globe2 size={19} />
                <input
                  autoCapitalize="none"
                  autoComplete="off"
                  id={`${kind}-domain`}
                  onChange={(event) => setDomain(event.target.value)}
                  placeholder="example.com"
                  required
                  spellCheck={false}
                  value={domain}
                />
              </div>
            </div>
            {kind === "dkim" ? (
              <div>
                <label htmlFor="dkim-selector">DKIM selector</label>
                <input
                  autoCapitalize="none"
                  autoComplete="off"
                  id="dkim-selector"
                  maxLength={63}
                  onChange={(event) => setSelector(event.target.value)}
                  placeholder="selector1"
                  required
                  spellCheck={false}
                  value={selector}
                />
                <small>Usually shown in your email provider's DKIM setup instructions.</small>
              </div>
            ) : null}
            <button className="button button--primary" disabled={!emailHealthEnabled || loading} type="submit">
              {loading ? "Checking DNS…" : `Check ${kind.toUpperCase()}`}
              {loading ? <RotateCcw className="health-spinner" size={18} /> : <Search size={18} />}
            </button>
            <p>Queries are limited to public DNS data. No mailbox or DNS credentials are requested.</p>
            {!emailHealthEnabled ? <p className="form-note form-note--pending">The public checker is temporarily unavailable.</p> : null}
            {error ? <p className="form-note form-note--error" role="alert">{error}</p> : null}
          </form>
        </div>
      </section>

      {result ? (
        <section className="free-tool-results" aria-live="polite">
          <div className="container">
            <div className="free-tool-results__heading">
              <div><span>Results for</span><h2>{result.domain}</h2></div>
              <p>Checked {new Date(result.checkedAt).toLocaleString()}</p>
            </div>
            <div className="free-tool-result-list">
              {checks.map((check) => (
                <article className={`free-tool-result free-tool-result--${check.status}`} key={check.id}>
                  <div className="free-tool-result__icon"><ToolStatusIcon status={check.status} /></div>
                  <div>
                    <span>{check.findingType}</span>
                    <h3>{check.title}</h3>
                    <p>{check.summary}</p>
                    <p>{check.detail}</p>
                    <div className="free-tool-recommendation"><strong>Next step</strong>{check.recommendation}</div>
                    {check.evidence.map((evidence) => (
                      <div className="free-tool-evidence" key={`${evidence.query}:${evidence.type}`}>
                        <div><span>{evidence.type}</span><code>{evidence.query}</code></div>
                        {evidence.records.length ? evidence.records.map((record) => (
                          <div className="free-tool-record" key={record}>
                            <code>{record}</code>
                            <button aria-label="Copy DNS record" onClick={() => void copy(record)} type="button">
                              {copied === record ? <CheckCircle2 size={15} /> : <Copy size={15} />}
                              {copied === record ? "Copied" : "Copy"}
                            </button>
                          </div>
                        )) : <p>No matching record returned.</p>}
                      </div>
                    ))}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>
      ) : null}
      <ToolEducation content={content} />
      <ToolLinks current={path} />
    </main>
  );
}

type DnsJson = { Status: number; Answer?: Array<{ data: string }> };
type ListResult = { name: string; status: "clear" | "listed" | "unavailable"; response?: string };

const ipLists = [
  ["SpamCop", "bl.spamcop.net"],
  ["PSBL", "psbl.surriel.com"],
  ["SpamRATS NoPtr", "noptr.spamrats.com"],
] as const;
const domainLists = [
  ["SURBL multi", "multi.surbl.org"],
  ["URIBL multi", "multi.uribl.com"],
] as const;

function ipv4Reverse(value: string) {
  const parts = value.trim().split(".");
  if (parts.length !== 4 || parts.some((part) => !/^\d{1,3}$/.test(part) || Number(part) > 255)) return null;
  return parts.reverse().join(".");
}

function normalizeDomain(value: string) {
  const candidate = value.trim().toLowerCase().replace(/^https?:\/\//, "").split(/[/?#]/)[0].replace(/\.$/, "");
  return /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/.test(candidate) ? candidate : null;
}

async function queryList(name: string, query: string): Promise<ListResult> {
  try {
    const response = await fetch(`https://dns.google/resolve?name=${encodeURIComponent(query)}&type=A`, {
      headers: { accept: "application/dns-json" },
    });
    if (!response.ok) return { name, status: "unavailable" };
    const data = await response.json() as DnsJson;
    if (data.Status === 3) return { name, status: "clear" };
    const answer = data.Answer?.map((item) => item.data).find((item) => /^127\./.test(item));
    if (answer === "127.0.0.1" || answer === "127.0.0.255") {
      return { name, status: "unavailable", response: answer };
    }
    if (data.Status === 0 && answer) return { name, status: "listed", response: answer };
    return { name, status: "unavailable" };
  } catch {
    return { name, status: "unavailable" };
  }
}

export function BlocklistCheckerPage({ kind }: { kind: BlocklistToolKind }) {
  const isIp = kind === "ip";
  const content = blocklistToolContent[kind];
  const path = `/tools/${kind}-blocklist-checker`;
  const [value, setValue] = useState("");
  const [checkedValue, setCheckedValue] = useState("");
  const [results, setResults] = useState<ListResult[]>([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalized = isIp ? ipv4Reverse(value) : normalizeDomain(value);
    if (!normalized) {
      setError(isIp ? "Enter a valid IPv4 address, such as 203.0.113.10." : "Enter a valid domain, such as example.com.");
      setResults([]);
      return;
    }
    setLoading(true);
    setError("");
    setResults([]);
    const lists = isIp ? ipLists : domainLists;
    const next = await Promise.all(lists.map(([name, zone]) => queryList(name, `${normalized}.${zone}`)));
    setCheckedValue(value.trim().toLowerCase());
    setResults(next);
    setLoading(false);
  }

  const listed = results.filter((result) => result.status === "listed").length;
  const available = results.filter((result) => result.status !== "unavailable").length;
  return (
    <main className="free-tool-page">
      <section className="free-tool-hero">
        <div className="container free-tool-hero__grid">
          <div className="free-tool-hero__copy">
            <ShieldCheck aria-hidden="true" />
            <h1>Check {isIp ? "an IP" : "a domain"} against public blocklists</h1>
            <p>See whether {isIp ? "a sending IPv4 address" : "a domain"} is currently returned by the public reputation lists queried by this tool.</p>
            <ul>
              <li><CheckCircle2 size={17} />No account required</li>
              <li><CheckCircle2 size={17} />Named sources shown</li>
              <li><CheckCircle2 size={17} />Unavailable lookups stay inconclusive</li>
            </ul>
          </div>
          <form className="free-tool-form" onSubmit={submit}>
            <div>
              <label htmlFor={`${kind}-blocklist`}>{isIp ? "IPv4 address" : "Domain name"}</label>
              <div className="free-tool-input">
                <Globe2 size={19} />
                <input
                  autoCapitalize="none"
                  autoComplete="off"
                  id={`${kind}-blocklist`}
                  onChange={(event) => setValue(event.target.value)}
                  placeholder={isIp ? "203.0.113.10" : "example.com"}
                  required
                  spellCheck={false}
                  value={value}
                />
              </div>
            </div>
            <button className="button button--primary" disabled={loading} type="submit">
              {loading ? "Checking lists…" : "Check blocklists"}
              {loading ? <RotateCcw className="health-spinner" size={18} /> : <Search size={18} />}
            </button>
            <p>This is a point-in-time lookup, not a guarantee of inbox placement or sender reputation.</p>
            {error ? <p className="form-note form-note--error" role="alert">{error}</p> : null}
          </form>
        </div>
      </section>
      {results.length ? (
        <section className="free-tool-results" aria-live="polite">
          <div className="container">
            <div className="free-tool-results__heading">
              <div><span>Results for</span><h2>{checkedValue}</h2></div>
              <p>{listed ? `${listed} listing${listed === 1 ? "" : "s"} found` : available ? "No listings returned by available sources" : "No source completed the lookup"}</p>
            </div>
            <div className="blocklist-results">
              {results.map((result) => (
                <article className={`blocklist-result blocklist-result--${result.status}`} key={result.name}>
                  {result.status === "clear" ? <CheckCircle2 /> : result.status === "listed" ? <CircleX /> : <CircleAlert />}
                  <div><h3>{result.name}</h3><p>{result.status === "clear" ? "No listing returned" : result.status === "listed" ? `Listed${result.response ? ` · ${result.response}` : ""}` : "Lookup unavailable — no conclusion"}</p></div>
                </article>
              ))}
            </div>
            <p className="blocklist-disclaimer">Blocklists have different policies and update schedules. Confirm any listing with the list operator before changing production mail infrastructure.</p>
          </div>
        </section>
      ) : null}
      <ToolEducation content={content} />
      <ToolLinks current={path} />
    </main>
  );
}
